import { QueryKey } from '@tanstack/react-query';
import ClientStateTrigger from './ClientStateTrigger';

import AcknowledgeIcon from '@/assets/icons/content/acknowledge.svg';
import { useQueryActions } from '@/hooks/useQueryActions';
import MercuryButton from '@/components/content/button/Button';
import { OIncidentsStatus } from '../../incidentmanagement/types';
import {
  compactIncidentActionButtonClassNames,
  type IncidentActionButtonVariant,
} from '../../incidentmanagement/components/incidentActionButton';

interface AcknowledgeProps {
  id: Array<[number, Array<string>]>;
  isAcknowledged: boolean;
  resource: string;
  invalidate?: Array<QueryKey>;
  disabled?: boolean;
  isLoading?: boolean;
  asButton?: boolean;
  buttonVariant?: IncidentActionButtonVariant;
}

const Acknowledge = ({
  id,
  isAcknowledged,
  invalidate,
  resource,
  disabled,
  isLoading,
  asButton,
  buttonVariant = 'default',
}: AcknowledgeProps) => {
  const actions = useQueryActions();

  const onSuccess = () => {
    if (invalidate && invalidate.length) {
      actions.invalidateQueries(invalidate, { type: 'all' });
    }
  };

  const label = isAcknowledged ? 'Unacknowledge' : 'Acknowledge';
  const nextStatus = isAcknowledged ? OIncidentsStatus.New : OIncidentsStatus.Acknowledged;
  const isCompactOutline = buttonVariant === 'compactOutline';

  return asButton ? (
    <ClientStateTrigger
      updateIds={id}
      resource={resource}
      disabled={disabled || isLoading}
      body={{ status: nextStatus }}
      onSuccess={onSuccess}
    >
      <MercuryButton
        size="sm"
        variant={isCompactOutline ? 'outline' : undefined}
        color={isCompactOutline ? '#454bd1' : 'var(--quo-green-50)'}
        loading={isLoading}
        rightSection={
          <AcknowledgeIcon width={isCompactOutline ? 16 : 15} height={isCompactOutline ? 16 : 15} />
        }
        classNames={isCompactOutline ? compactIncidentActionButtonClassNames : undefined}
      >
        {label}
      </MercuryButton>
    </ClientStateTrigger>
  ) : (
    <ClientStateTrigger
      updateIds={id}
      icon={<AcknowledgeIcon />}
      label={label}
      color={isAcknowledged ? 'var(--ds-status-success)' : 'var(--ds-font-color)'}
      resource={resource}
      disabled={disabled || isLoading}
      body={{ status: nextStatus }}
      onSuccess={onSuccess}
    />
  );
};

export default Acknowledge;
